import { useMemo } from "react";
import {
  cn,
  Column,
  DataTable,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Separator,
} from "@racoon/ui";
import { Namespace, TranslationKey, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetTruckTypeByIdQuery } from "~/shared/api/truck/truck-type.api";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  truck: ConnectProto.Truck | null;
};

type MatrixRow = {
  containerTypeId: string;
  containerTypeName: string;
};

/** Read-only view of the selected truck's TruckCapabilityMatrix (container type × job type). */
export const TruckCapabilitiesModal = ({ open, onOpenChange, truck }: Props) => {
  const t = useT();

  const truckTypeId = truck?.truckType?.id;
  const { data: truckType, isFetching } = useGetTruckTypeByIdQuery(
    open && truck && truckTypeId
      ? { id: truckTypeId, ownerOrganizationId: truck.ownerOrganizationId }
      : skipToken,
  );

  // capabilityByCt[containerTypeId][jobType] = true
  const { capabilityByCt, jobTypes } = useMemo(() => {
    const byCt = new Map<string, Set<ConnectProto.JobType>>();
    const jobTypeSet = new Set<ConnectProto.JobType>();
    (truckType?.truckTypeCapabilities ?? []).forEach((cap) => {
      const ctId = cap.containerType?.id;
      if (!ctId || !cap.jobType) return;
      jobTypeSet.add(cap.jobType);
      const set = byCt.get(String(ctId)) ?? new Set<ConnectProto.JobType>();
      set.add(cap.jobType);
      byCt.set(String(ctId), set);
    });
    return {
      capabilityByCt: byCt,
      jobTypes: Array.from(jobTypeSet).sort((a, b) => a - b),
    };
  }, [truckType]);

  // Only container types present in the capability matrix.
  const rows = useMemo((): MatrixRow[] => {
    const seen = new Map<string, string>();
    (truckType?.truckTypeCapabilities ?? []).forEach((cap) => {
      const ct = cap.containerType;
      if (ct) seen.set(String(ct.id), ct.name || ct.volume?.toString() || `#${ct.id}`);
    });
    return Array.from(seen, ([containerTypeId, containerTypeName]) => ({
      containerTypeId,
      containerTypeName,
    }));
  }, [truckType]);

  const columns = useMemo((): Column<MatrixRow>[] => {
    const cols: Column<MatrixRow>[] = [
      {
        id: "containerType",
        header: t(Namespace.PickupLocation, "groups.capabilitiesModal.containerType"),
        key: "containerTypeName",
        align: "center",
        width: 150,
      },
    ];
    jobTypes.forEach((jt) => {
      cols.push({
        id: `job_type_${jt}`,
        header: t(Namespace.Enum, `JOB_TYPE_${ConnectProto.JobType[jt]}` as TranslationKey),
        align: "center",
        width: 140,
        // A static dot rather than a disabled checkbox: this grid is a read-only
        // view, and a disabled control renders at half opacity, which reads as
        // "loading" instead of "not supported".
        cell: ({ row }) => (
          <span className="flex w-full justify-center">
            <span
              className={cn(
                "inline-block h-4 w-4 rounded-full",
                capabilityByCt.get(row.original.containerTypeId)?.has(jt)
                  ? "bg-brand-blue"
                  : "bg-[#D9D9D9]",
              )}
            />
          </span>
        ),
      });
    });
    return cols;
  }, [t, jobTypes, capabilityByCt]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Opened from inside another modal, so it adds only a light dim of its
        // own — two full overlays stack into a near-black backdrop.
        dim="light"
        className="!gap-0 !p-0 max-h-[80vh] overflow-hidden rounded-2xl sm:max-w-[1500px]"
      >
        <DialogHeader className="!px-4">
          <DialogTitle className="text-xl font-bold tracking-tight">
            {t(Namespace.PickupLocation, "groups.truckCapabilities")} {truck?.plateNumber ?? ""}
          </DialogTitle>
        </DialogHeader>

        <Separator />

        <div className="overflow-auto p-4">
          {isFetching ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              {t(Namespace.PickupLocation, "loading")}
            </div>
          ) : jobTypes.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              {t(Namespace.PickupLocation, "groups.capabilitiesModal.empty")}
            </div>
          ) : (
            <DataTable<MatrixRow>
              columns={columns}
              data={rows}
              rowKey={(row) => row.containerTypeId}
              zebra={false}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
